// PLACEHOLDER — Income Estimate (demo)
//
// Synthetic but realistic income projection data for the Income Estimate
// demo page. Ported from `income-data.js` in the design bundle
// (finance-planner-ronny-lockin/project). No backend wiring — this is the
// fixed dataset sales/admins use to demo the page to prospects.
//
// When this feature ships for real, REPLACE THIS FILE with a composable
// (e.g. `useIncomeEstimate(companyId)`) that fetches from a real endpoint.
// The backend building blocks already exist: production projection ×
// allocation simulation × per-stream prices × Soldera fee. The shape
// exposed here (Stream[], categoryMonthly, categoryVariableMonthly,
// totals, MONTHS) is a useful reference for the eventual API contract.

/**
 * How sure are we about the income from this stream?
 * - `fixed`: locked-in contract with both price and volume known up front.
 * - `estimated`: price is fixed but volume tracks projected production
 *   (e.g. pay-as-produced PPA, floor-priced biomass).
 * - `speculative`: neither price nor volume is contracted — projection
 *   only (e.g. residual spot sales, indicative future forward blocks).
 */
export type Certainty = "fixed" | "estimated" | "speculative";

/** Income stream category — drives chart stack ordering and color. */
export type Category = "PPA" | "Forward" | "Spot";

/**
 * Horizon picker value. The dataset is anchored to Jun 2026 so:
 * - `current-year` shows the remaining 7 months of 2026
 * - `next-12` shows Jun 2026 – May 2027
 * - `two-year` shows the full 24-month window (Jun 2026 – May 2028)
 */
export type HorizonValue = "current-year" | "next-12" | "two-year";

/** A single month-axis tick on the chart and table. */
export interface MonthLabel {
  /** ISO-ish month key, e.g. `"2026-06"`. */
  key: string;
  /** Three-letter month abbreviation, e.g. `"Jun"`. */
  short: string;
  /** Two-digit year, e.g. `"26"` for 2026. */
  year: string;
}

/**
 * One row of the per-stream table — a contract or sales channel that
 * generates monthly income across the 24-month horizon.
 *
 * `gross`/`net`/`mwh` are always length-24 arrays even when only a few
 * months have non-zero values (e.g. quarterly forward payouts land in a
 * single month).
 */
export interface Stream {
  /** Stable identifier — used as the Vue `key` on table rows. */
  id: string;
  /** PPA / Forward / Spot — drives stacking order and color. */
  category: Category;
  /** Customer/contract name shown in the first column of the table. */
  name: string;
  /** Single-line description: term + technology + duration. */
  subtitle: string;
  /** See {@link Certainty}. Controls the cert-pip label + hatched fill. */
  certainty: Certainty;
  /** Contract or projected price per MWh, in EUR. */
  pricePerMWh: number;
  /** Human-readable pricing note, e.g. `"Locked €1.55/MWh, payout Jul 2026"`. */
  pricingNote: string;
  /** `fixed` = same volume every month; `variable` = follows production curve. */
  volumeKind: "fixed" | "variable";
  /** Volume per month in MWh (length 24). */
  mwh: number[];
  /** Gross income per month in EUR (= `mwh * pricePerMWh`). */
  gross: number[];
  /** Net income per month in EUR (= `gross * (1 - SOLDERA_FEE_PCT)`). */
  net: number[];
}

/**
 * Configuration for the "Configure price assumptions" modal. Each price
 * is in EUR/MWh; `override` is null when the user hasn't set one.
 */
export interface SpotPrices {
  renewable: {
    /** Currently applied price. */
    current: number;
    /** Weighted average of the last 3 sales (recommended default). */
    weighted3: number;
    /** Weighted average of the last 6 sales (smoother). */
    weighted6: number;
    /** Most recent single sale price (aggressive). */
    lastSale: number;
    /** Manual override; null until the user enters one. */
    override: number | null;
  };
  /** Which pricing-method radio is selected. */
  method: "weighted3" | "weighted6" | "lastSale" | "override";
}

export const SOLDERA_FEE_PCT = 0.1;

/**
 * How many months a given horizon should render.
 *
 * The dataset is anchored to Jun 2026:
 * - `current-year` → 7  (rest of 2026)
 * - `next-12`      → 12 (Jun 2026 – May 2027)
 * - `two-year`     → 24 (full window, Jun 2026 – May 2028)
 */
export function monthsForHorizon(horizon: HorizonValue): number {
  if (horizon === "current-year") return 7;
  if (horizon === "next-12") return 12;
  return 24;
}

/**
 * Aggregate net totals across the first `monthCount` months of the
 * horizon — used by the KPI strip so the displayed total/fixed/variable
 * always matches the chart and table for the chosen horizon, not the
 * precomputed 12/24 partition.
 *
 * @param streams Stream rows produced by {@link buildDemoStreams}.
 * @param monthCount Number of leading months to sum across (≤ 24).
 * @returns `{ total, fixed, variable }` net EUR over the slice.
 */
export function totalsForMonths(
  streams: Stream[],
  monthCount: number
): { total: number; fixed: number; variable: number } {
  let total = 0;
  let fixed = 0;
  let variable = 0;
  for (const s of streams) {
    for (let i = 0; i < monthCount; i++) {
      total += s.net[i];
      if (s.certainty === "fixed") fixed += s.net[i];
      else variable += s.net[i];
    }
  }
  return { total, fixed, variable };
}

export const CATEGORIES: Category[] = ["PPA", "Forward", "Spot"];

// Forest-700 / Forest-500 / Ochre-500 from the design tokens.
export const CATEGORY_COLORS: Record<Category, string> = {
  PPA: "#0d2218",
  Forward: "#1f4d33",
  Spot: "#c08e3a",
};

// 24-month label list, anchored to current month (May 2026) — forward-looking
// window starts at Jun 2026 and runs through May 2028.
export const MONTHS: MonthLabel[] = [
  { key: "2026-06", short: "Jun", year: "26" },
  { key: "2026-07", short: "Jul", year: "26" },
  { key: "2026-08", short: "Aug", year: "26" },
  { key: "2026-09", short: "Sep", year: "26" },
  { key: "2026-10", short: "Oct", year: "26" },
  { key: "2026-11", short: "Nov", year: "26" },
  { key: "2026-12", short: "Dec", year: "26" },
  { key: "2027-01", short: "Jan", year: "27" },
  { key: "2027-02", short: "Feb", year: "27" },
  { key: "2027-03", short: "Mar", year: "27" },
  { key: "2027-04", short: "Apr", year: "27" },
  { key: "2027-05", short: "May", year: "27" },
  { key: "2027-06", short: "Jun", year: "27" },
  { key: "2027-07", short: "Jul", year: "27" },
  { key: "2027-08", short: "Aug", year: "27" },
  { key: "2027-09", short: "Sep", year: "27" },
  { key: "2027-10", short: "Oct", year: "27" },
  { key: "2027-11", short: "Nov", year: "27" },
  { key: "2027-12", short: "Dec", year: "27" },
  { key: "2028-01", short: "Jan", year: "28" },
  { key: "2028-02", short: "Feb", year: "28" },
  { key: "2028-03", short: "Mar", year: "28" },
  { key: "2028-04", short: "Apr", year: "28" },
  { key: "2028-05", short: "May", year: "28" },
];

// Solar production curve indexed Jun..May (rotated +5 from calendar Jan..Dec)
const SOLAR_CURVE = [
  1.45, 1.5, 1.32, 1.05, 0.72, 0.45, 0.32, 0.42, 0.55, 0.85, 1.1, 1.35,
];
// Biomass — flatter, slight winter peak, also rotated to Jun..May
const BIOMASS_CURVE = [
  0.85, 0.85, 0.9, 0.95, 1.05, 1.12, 1.13, 1.15, 1.1, 1.05, 0.95, 0.9,
];

interface StreamSpec {
  id: string;
  category: Category;
  name: string;
  subtitle: string;
  certainty: Certainty;
  pricePerMWh: number;
  pricingNote: string;
  volumeKind: "fixed" | "variable";
  monthlyMWh: () => number[];
}

function streamSpecs(): StreamSpec[] {
  const solarPair = SOLAR_CURVE.concat(SOLAR_CURVE);
  const biomassPair = BIOMASS_CURVE.concat(BIOMASS_CURVE);
  return [
    {
      id: "ppa-nordsee",
      category: "PPA",
      name: "Nordsee Industrie GmbH",
      subtitle: "10-yr fixed PPA · Solar · 2024–2034",
      certainty: "fixed",
      pricePerMWh: 3.4,
      pricingNote: "Fixed €3.40/MWh, contractual",
      volumeKind: "fixed",
      monthlyMWh: () => Array(24).fill(720),
    },
    {
      id: "ppa-vattenmark",
      category: "PPA",
      name: "Vattenmark Energi AB",
      subtitle: "5-yr pay-as-produced · Solar · 2025–2030",
      certainty: "estimated",
      pricePerMWh: 2.2,
      pricingNote: "Fixed €2.20/MWh, volume tracks production",
      volumeKind: "variable",
      monthlyMWh: () => solarPair.map((m) => Math.round(620 * m)),
    },
    {
      id: "ppa-hello-energy",
      category: "PPA",
      name: "Hello Energy Retail",
      subtitle: "3-yr floor-priced · Biomass · 2025–2028",
      certainty: "estimated",
      pricePerMWh: 1.8,
      pricingNote: "Floor €1.80/MWh + 30% market upside",
      volumeKind: "variable",
      monthlyMWh: () => biomassPair.map((m) => Math.round(380 * m)),
    },
    {
      id: "fwd-2026-q2",
      category: "Forward",
      name: "2026 Q2 forward block",
      subtitle: "Forward · 9 800 MWh · quarterly block · paid Jul 2026",
      certainty: "fixed",
      pricePerMWh: 1.55,
      pricingNote: "Locked €1.55/MWh, payout Jul 2026",
      volumeKind: "fixed",
      monthlyMWh: () => {
        const a = Array(24).fill(0);
        a[1] = 9800;
        return a;
      },
    },
    {
      id: "fwd-2026-q3",
      category: "Forward",
      name: "2026 Q3 forward block",
      subtitle: "Forward · 11 200 MWh · quarterly block · paid Oct 2026",
      certainty: "fixed",
      pricePerMWh: 1.7,
      pricingNote: "Locked €1.70/MWh, payout Oct 2026",
      volumeKind: "fixed",
      monthlyMWh: () => {
        const a = Array(24).fill(0);
        a[4] = 11200;
        return a;
      },
    },
    {
      id: "fwd-2026-q4",
      category: "Forward",
      name: "2026 Q4 forward block",
      subtitle: "Forward · 8 400 MWh · quarterly block · paid Jan 2027",
      certainty: "fixed",
      pricePerMWh: 1.6,
      pricingNote: "Locked €1.60/MWh, payout Jan 2027",
      volumeKind: "fixed",
      monthlyMWh: () => {
        const a = Array(24).fill(0);
        a[7] = 8400;
        return a;
      },
    },
    {
      id: "fwd-2027-tentative",
      category: "Forward",
      name: "2027 indicative forward volumes",
      subtitle: "Provisional · ~40% planned · target €1.85/MWh",
      certainty: "speculative",
      pricePerMWh: 1.85,
      pricingNote: "Target €1.85/MWh — not yet contracted",
      volumeKind: "variable",
      monthlyMWh: () => {
        const a = Array(24).fill(0);
        a[10] = 6000;
        a[13] = 5400;
        a[16] = 5800;
        a[19] = 5200;
        return a;
      },
    },
    {
      id: "spot-renewable",
      category: "Spot",
      name: "Spot · Any Renewable",
      subtitle: "Residual production sold monthly across all technologies",
      certainty: "speculative",
      pricePerMWh: 1.72,
      pricingNote: "Last-3-sale weighted avg €1.72/MWh",
      volumeKind: "variable",
      monthlyMWh: () =>
        solarPair.map(
          (m, i) => Math.round(1850 * m) + Math.round(1100 * biomassPair[i])
        ),
    },
  ];
}

/**
 * Materialize the demo dataset: walk every spec, expand monthly volumes,
 * then compute gross (mwh × price) and net (gross less {@link SOLDERA_FEE_PCT})
 * per month. Always returns 24-month rows.
 */
export function buildDemoStreams(): Stream[] {
  return streamSpecs().map((s) => {
    const mwh = s.monthlyMWh();
    const gross = mwh.map((v) => v * s.pricePerMWh);
    const net = gross.map((v) => v * (1 - SOLDERA_FEE_PCT));
    return {
      id: s.id,
      category: s.category,
      name: s.name,
      subtitle: s.subtitle,
      certainty: s.certainty,
      pricePerMWh: s.pricePerMWh,
      pricingNote: s.pricingNote,
      volumeKind: s.volumeKind,
      mwh,
      gross,
      net,
    };
  });
}

/**
 * Net income per category per month (length-24 arrays) — drives the
 * stacked bars in the chart and the group rows in the table.
 */
export function categoryMonthly(streams: Stream[]): Record<Category, number[]> {
  const result = {
    PPA: Array(24).fill(0),
    Forward: Array(24).fill(0),
    Spot: Array(24).fill(0),
  } as Record<Category, number[]>;
  for (const s of streams) {
    for (let i = 0; i < 24; i++) result[s.category][i] += s.net[i];
  }
  return result;
}

/**
 * Net income per category per month, restricted to non-`fixed` streams.
 * This is the "estimated portion" of each stack — rendered with the 45°
 * hatched overlay in the chart.
 */
export function categoryVariableMonthly(
  streams: Stream[]
): Record<Category, number[]> {
  const result = {
    PPA: Array(24).fill(0),
    Forward: Array(24).fill(0),
    Spot: Array(24).fill(0),
  } as Record<Category, number[]>;
  for (const s of streams) {
    if (s.certainty === "fixed") continue;
    for (let i = 0; i < 24; i++) result[s.category][i] += s.net[i];
  }
  return result;
}

export const DEFAULT_SPOT_PRICES: SpotPrices = {
  renewable: {
    current: 1.72,
    weighted3: 1.72,
    weighted6: 1.6,
    lastSale: 1.88,
    override: null,
  },
  method: "weighted3",
};

/**
 * Format a whole-number EUR amount with European thousand separators
 * (non-breaking space, per the design spec). `fmt(1234567)` → `"1 234 567"`.
 */
export const fmt = (n: number): string =>
  Math.round(n).toLocaleString("en-US").replace(/,/g, " ");

/**
 * Compact representation for table/legend cells. Keeps one decimal
 * between 1k and 10k so small monthly figures (e.g. €2,203) render as
 * `"2.2k"` instead of being aggressively rounded to `"2k"` (which would
 * lose up to ~33% of precision). Values < 1k use plain {@link fmt}.
 */
export const fmtK = (n: number): string => {
  const abs = Math.abs(n);
  if (abs >= 10000) return fmt(n / 1000) + "k";
  if (abs >= 1000) {
    return (n / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  }
  return fmt(n);
};

/**
 * Cell formatter for the per-stream monthly table. Renders an em-dash
 * placeholder when the value is below the rounding threshold, otherwise
 * delegates to {@link fmtK} for the compact representation.
 */
export const fmtCell = (n: number): string => (n < 1 ? "—" : fmtK(n));
